/* global __BUILD_TAG__ */
import { useEffect, useState } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation, matchPath } from 'react-router-dom';
import {
  OFERTA_PATH,
  OPERATOR_EMAIL,
  OPERATOR_INN,
  OPERATOR_NAME,
  OPERATOR_OGRNIP,
} from '../legal/operator';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { WorkChromeProvider, useWorkChrome } from '../context/WorkChromeContext';
import WorkToolbar from './WorkToolbar';
import UserAccountMenu from './UserAccountMenu';
import CookieNotice from './CookieNotice';
import api from '../api/client';

function showThematicMenuLinks(user) {
  if (!user) return false;
  return Boolean(
    user.is_student || user.is_teacher || user.is_staff || user.is_admin
  );
}

const BUILD_TAG = typeof __BUILD_TAG__ !== 'undefined' ? __BUILD_TAG__ : 'режим разработки (без сборки)';

function isCollabChromePath(pathname) {
  return Boolean(
    matchPath({ path: '/collaborative', end: true }, pathname) ||
      matchPath({ path: '/collaborative/:sessionId', end: true }, pathname),
  );
}

function LayoutInner() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { toolbarProps } = useWorkChrome();
  const isDark = theme === 'dark';
  const collabChrome = isCollabChromePath(location.pathname);
  const [topicMenu, setTopicMenu] = useState([]);

  useEffect(() => {
    if (collabChrome || !showThematicMenuLinks(user)) {
      setTopicMenu([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/tasks/topic-programs/menu/');
        const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        if (!cancelled) setTopicMenu(data);
      } catch {
        if (!cancelled) setTopicMenu([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, collabChrome]);

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch {
      navigate('/login');
    }
  }

  const isTeacherCabinet = Boolean(user?.is_teacher || user?.is_staff || user?.is_admin);

  return (
    <div className={`layout${collabChrome ? ' layout--collab-chrome' : ''}`}>
      <header className="header">
        <div className="header-inner">
          <NavLink to="/" className="logo" title="Главная">
            <span className="logo-icon">📘</span>
            <span className="logo-text">
              diobuch{collabChrome ? null : <span className="logo-version"> v2.0</span>}
            </span>
          </NavLink>

          {collabChrome ? (
            <nav className="nav nav--collab-chrome" aria-label="Быстрая навигация">
              <NavLink to="/" end className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
                Главная
              </NavLink>
              {user?.is_student && (
                <NavLink
                  to="/konspekty"
                  className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                >
                  Конспекты
                </NavLink>
              )}
              {isTeacherCabinet && (
                <NavLink
                  to="/teacher"
                  className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                >
                  Кабинет преподавателя
                </NavLink>
              )}
            </nav>
          ) : (
            <nav className="nav">
              <NavLink to="/" end className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
                Главная
              </NavLink>
              {user?.is_student && (
                <NavLink to="/tasks" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
                  Задачи
                </NavLink>
              )}
              {user && (
                <>
                  <NavLink
                    to="/practice-quizzes"
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                  >
                    Пробные тесты
                  </NavLink>
                  {topicMenu.map((p) => (
                    <NavLink
                      key={p.slug}
                      to={`/themes/${p.slug}`}
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      {p.label || p.slug}
                    </NavLink>
                  ))}
                  {user?.is_student && (
                    <NavLink
                      to="/my-teachers"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Мои преподаватели
                    </NavLink>
                  )}
                  {user?.is_student && (
                    <NavLink
                      to="/topic-progress"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Прогресс по темам
                    </NavLink>
                  )}
                  {user?.is_student && (
                    <NavLink
                      to="/my-attempts"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Мои попытки
                    </NavLink>
                  )}
                  {isTeacherCabinet && (
                    <NavLink
                      to="/teacher"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Кабинет преподавателя
                    </NavLink>
                  )}
                  {user?.is_parent && (
                    <NavLink
                      to="/parent"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Дети
                    </NavLink>
                  )}
                  {user && (
                    <NavLink
                      to="/billing"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Баланс
                    </NavLink>
                  )}
                  {user?.is_student && (
                    <NavLink
                      to="/dashboard"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Мой прогресс
                    </NavLink>
                  )}
                  <NavLink
                    to="/konspekty"
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                  >
                    Конспект
                  </NavLink>
                  <NavLink
                    to="/collaborative"
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                  >
                    Совместное кодирование
                  </NavLink>
                  {isTeacherCabinet && (
                    <NavLink
                      to="/teacher/billing"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Оплата учеников
                    </NavLink>
                  )}
                  {isTeacherCabinet && (
                    <NavLink
                      to="/teacher/tasks"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Конструктор задач
                    </NavLink>
                  )}
                  {isTeacherCabinet && (
                    <NavLink
                      to="/teacher/tags"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Теги
                    </NavLink>
                  )}
                  {(user.is_staff || user.is_admin) && (
                    <NavLink
                      to="/admin/topic-programs"
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      Программы (темы)
                    </NavLink>
                  )}
                </>
              )}
            </nav>
          )}

          {collabChrome && toolbarProps ? (
            <WorkToolbar {...toolbarProps} className="work-toolbar--chrome" />
          ) : null}

          <div className="header-actions">
            <div className="header-theme-toggle" role="group" aria-label="Тема сайта">
              <button
                type="button"
                className={`btn btn-outline btn-xs btn-icon-only ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
                title="Светлая тема сайта"
                aria-label="Светлая тема сайта"
              >
                <i className="fa-solid fa-sun ui-icon" aria-hidden />
              </button>
              <button
                type="button"
                className={`btn btn-outline btn-xs btn-icon-only ${isDark ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
                title="Тёмная тема сайта"
                aria-label="Тёмная тема сайта"
              >
                <i className="fa-solid fa-moon ui-icon" aria-hidden />
              </button>
            </div>
            {user ? (
              <UserAccountMenu user={user} onLogout={handleLogout} />
            ) : (
              <div className="auth-links">
                <NavLink to="/login" className="btn btn-outline btn-sm">
                  Войти
                </NavLink>
                <NavLink to="/register" className="btn btn-primary btn-sm">
                  Регистрация
                </NavLink>
              </div>
            )}
          </div>

          {!collabChrome && (
            <button
              type="button"
              className="mobile-menu-btn"
              onClick={() => {
                document.querySelector('.nav')?.classList.toggle('nav-open');
              }}
            >
              ☰
            </button>
          )}
        </div>
      </header>
      <main className="main-content">
        <Outlet />
      </main>
      {!collabChrome && (
        <footer className="footer">
          <p className="footer-operator">
            Оператор ПДн: {OPERATOR_NAME}
            {' · '}ИНН {OPERATOR_INN}
            {' · '}ОГРНИП {OPERATOR_OGRNIP}
          </p>
          <p className="footer-operator">
            Запросы субъектов ПДн:{' '}
            <a href={`mailto:${OPERATOR_EMAIL}`}>{OPERATOR_EMAIL}</a>
          </p>
          <nav className="footer-links" aria-label="Документы">
            <Link to="/privacy">Политика обработки ПДн</Link>
            <Link to="/consent">Согласие на обработку ПДн</Link>
            <Link to={OFERTA_PATH}>Договор-оферта</Link>
          </nav>
          <p className="footer-cookies">
            Сайт использует только необходимые cookie (сессия и защита форм).
            Аналитических и рекламных cookie нет.
          </p>
          <p>© 2026 diobuch — подготовка к ЕГЭ/ОГЭ по информатике</p>
          <p className="footer-build">Обновлено: {BUILD_TAG}</p>
        </footer>
      )}
      <CookieNotice />
    </div>
  );
}

export default function Layout() {
  return (
    <WorkChromeProvider>
      <LayoutInner />
    </WorkChromeProvider>
  );
}
